import { useEffect, useRef, useState } from 'react'
import { HubConnectionBuilder, HubConnectionState, HubConnection } from '@microsoft/signalr'
import { getAuthToken } from '~shared/util/authUtil'

type UseSignalROptions = {
  enabled: boolean
  notificationsUrl: string | undefined
  onOrdersUpdated?: () => void
  onSiteHealthChanged?: () => void
}

// withAutomaticReconnect() gives up after ~42s. Beyond that we rebuild manually,
// capped at ~2h so a permanently broken backend doesn't loop forever.
const MAX_REBUILD_ATTEMPTS = 18
const rebuildBackoffMs = (attempt: number) => Math.min(600_000, 5_000 * Math.pow(2, attempt))

export function useSignalR({
  enabled,
  notificationsUrl,
  onOrdersUpdated,
  onSiteHealthChanged,
}: UseSignalROptions) {
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(null)
  const [buildEpoch, setBuildEpoch] = useState(0)
  const consecutiveFailuresRef = useRef(0)

  // Refs so Effect 2 reads the latest callbacks without listing them as deps —
  // otherwise inline lambdas would tear down and re-negotiate on every parent render.
  const handlersRef = useRef({ onOrdersUpdated, onSiteHealthChanged })
  handlersRef.current = { onOrdersUpdated, onSiteHealthChanged }

  useEffect(() => {
    if (!enabled) return
    if (!notificationsUrl) return

    const newConnection = new HubConnectionBuilder()
      .withUrl(`${notificationsUrl}/hub`, {
        accessTokenFactory: () => getAuthToken() ?? '',
      })
      .withAutomaticReconnect()
      .build()

    setHubConnection(newConnection)
  }, [enabled, notificationsUrl, buildEpoch])

  useEffect(() => {
    if (enabled || !hubConnection) return
    setHubConnection(null)
  }, [enabled, hubConnection])

  useEffect(() => {
    if (!hubConnection || !enabled) return

    const onGroupMessage = (
      _message: unknown,
      type: string,
      recordId: string,
      _recordUid: string
    ) => {
      switch (type) {
        case 'ClientLab':
          if (recordId === 'DeptOrdersSignalR') {
            window.location.reload()
          }
          break
        case 'NotificationType':
          handlersRef.current.onOrdersUpdated?.()
          break
        case 'SiteHealthUpdated':
          handlersRef.current.onSiteHealthChanged?.()
          break
      }
    }

    // `disposed` guards against the cleanup-triggered onclose queueing a rebuild
    // after intentional teardown (unmount, disable, rebuild swap).
    let disposed = false
    let rebuildTimer: ReturnType<typeof setTimeout> | undefined
    const scheduleRebuild = () => {
      if (disposed) return
      const attempt = consecutiveFailuresRef.current
      if (attempt >= MAX_REBUILD_ATTEMPTS) return
      consecutiveFailuresRef.current = attempt + 1
      rebuildTimer = setTimeout(() => {
        setBuildEpoch(e => e + 1)
      }, rebuildBackoffMs(attempt))
    }
    hubConnection.onclose(scheduleRebuild)

    // Failure counter only resets once we're fully joined — a connected-but-no-groups
    // state isn't healthy, and treating it as such would prevent backoff growth across
    // repeated JoinGroup failures. A failed join also routes through scheduleRebuild
    // since auto-reconnect leaves us connected-but-deaf otherwise.
    const joinGroup = () => {
      hubConnection
        .send('JoinGroup')
        .then(() => {
          consecutiveFailuresRef.current = 0
        })
        .catch(error => {
          console.error('SignalR JoinGroup failed:', error)
          scheduleRebuild()
        })
    }

    hubConnection.onreconnected(joinGroup)

    hubConnection
      .start()
      .then(() => {
        hubConnection.on('groupMessage', onGroupMessage)
        joinGroup()
      })
      .catch(() => {
        // start() failure doesn't fire onclose; trigger the rebuild ourselves.
        scheduleRebuild()
      })

    return () => {
      disposed = true
      if (rebuildTimer !== undefined) clearTimeout(rebuildTimer)
      hubConnection.off('groupMessage', onGroupMessage)
      if (hubConnection.state !== HubConnectionState.Disconnected) {
        hubConnection.stop().catch(() => {})
      }
    }
  }, [hubConnection])
}
